import { EventService } from "../../../services/event.service.js";

/**
 * EventBanner Component
 * 
 * Displays active Golden Hour events in the Plaza.
 * Supports Single Mode (Static) and Carousel Mode (Multiple Events).
 * Handles its own EventService subscription.
 */
export class EventBanner {
    constructor() {
        this.element = document.createElement('div');
        this.element.className = 'hidden mb-6'; // Initial hidden state
        this._unsubscribe = null;
        this._carouselInterval = null;
    }

    render() {
        this._unsubscribe = EventService.subscribe(() => this.update());
        return this.element;
    }

    destroy() {
        if (this._unsubscribe) this._unsubscribe();
        if (this._carouselInterval) clearInterval(this._carouselInterval);
    }

    update() {
        const activeEvents = EventService.getActiveGoldenTimeEvents();
        const container = this.element;

        // Clear existing content and events
        container.innerHTML = '';
        container.onclick = null;
        if (this._carouselInterval) clearInterval(this._carouselInterval);

        if (activeEvents.length === 0) {
            container.classList.add('hidden');
            return;
        }

        container.classList.remove('hidden');

        if (activeEvents.length === 1) {
            this.renderSingle(activeEvents[0]);
        } else {
            this.renderCarousel(activeEvents);
        }
    }

    renderSingle(event) {
        // [FIX] Update Banner Title Format: [Title] + [Series Title]
        // User Request: "Golden Hour event... title is output incorrectly"
        let displayHtml = event.bannerText || `[${event.title}] ${event.seriesTitle}`;

        if (event.multiplier) {
            displayHtml = displayHtml.replace(/화력 \d+배/g, `화력 ${event.multiplier}배`);
            displayHtml = displayHtml.replace(/x\d+/gi, `x${event.multiplier}`);
        }

        const theme = EventService.getEventTheme(event.multiplier);

        // Tailwind Classes
        this.element.className = `mb-6 relative rounded-xl shadow-lg overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-transform bg-gradient-to-r ${theme.toastGradient || 'from-yellow-400 via-orange-500 to-red-500'} text-white p-3 flex items-center justify-center gap-2 font-bold animate-pulse`;

        this.element.innerHTML = `
            <span class="material-symbols-outlined fill-current">bolt</span>
            ${displayHtml}
            <span class="bg-white/20 px-2 py-0.5 rounded text-xs ml-2">HOT</span>
        `;

        this.element.onclick = () => window.router.navigate(`/series/${event.seriesId}`);
    }

    renderCarousel(events) {
        this.element.className = "mb-6 relative h-12 rounded-xl shadow-lg overflow-hidden cursor-pointer transform hover:scale-[1.01] transition-transform bg-zinc-800";

        let currentIndex = 0;
        const total = events.length;
        const slidesContainer = document.createElement('div');
        slidesContainer.className = "absolute inset-0";

        events.forEach((event, idx) => {
            const slide = document.createElement('div');
            // Transition classes
            slide.className = `absolute inset-0 flex items-center justify-center font-bold text-white transition-opacity duration-500 ${idx === 0 ? 'opacity-100' : 'opacity-0'}`;

            const theme = EventService.getEventTheme(event.multiplier);
            slide.className += ` bg-gradient-to-r ${theme.toastGradient || 'from-zinc-700 to-zinc-900'}`;

            // [FIX] Update Banner Title Format: [Title] + [Series Title]
            let displayHtml = event.bannerText || `[${event.title}] ${event.seriesTitle}`;

            if (event.multiplier) {
                displayHtml = displayHtml.replace(/화력 \d+배/g, `화력 ${event.multiplier}배`);
                displayHtml = displayHtml.replace(/x\d+/gi, `x${event.multiplier}`);
            }

            slide.innerHTML = `
                 <span class="material-symbols-outlined fill-current mr-2 animate-bounce">bolt</span>
                 <span class="mr-2">${displayHtml}</span>
                 <span class="bg-white/20 px-2 py-0.5 rounded text-xs">x${event.multiplier}</span>
            `;
            // Clicks on slide
            slide.onclick = (e) => {
                e.stopPropagation();
                window.router.navigate(`/series/${event.seriesId}`);
            };

            slidesContainer.appendChild(slide);
        });
        this.element.appendChild(slidesContainer);

        // Dots
        const dotsContainer = document.createElement('div');
        dotsContainer.className = "absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1 z-10 pointer-events-none";
        for (let i = 0; i < total; i++) {
            const dot = document.createElement('div');
            dot.className = `w-1 h-1 rounded-full bg-white/50 transition-all ${i === 0 ? 'bg-white w-3' : ''}`;
            dotsContainer.appendChild(dot);
        }
        this.element.appendChild(dotsContainer);

        // Auto Rotate Logic
        const showSlide = (index) => {
            Array.from(slidesContainer.children).forEach((slide, i) => {
                slide.style.opacity = i === index ? '1' : '0';
                slide.style.pointerEvents = i === index ? 'auto' : 'none';
            });
            Array.from(dotsContainer.children).forEach((dot, i) => {
                dot.className = `w-1 h-1 rounded-full bg-white/50 transition-all ${i === index ? 'bg-white w-3' : ''}`;
            });
            // Update Main Onclick to current
            this.element.onclick = () => window.router.navigate(`/series/${events[index].seriesId}`);
            currentIndex = index;
        };

        this._carouselInterval = setInterval(() => {
            showSlide((currentIndex + 1) % total);
        }, 4000);
    }
}
